import CT from '@marketplace-ai/tags';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Pill } from '~/components';
import { dateComparator } from '~/components/agGrid/columns/dateColumns';
import { createValidCol } from '~/components/agGrid/columns/sharedColumns';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { formatCredentialDate } from '~/features/utils';
import { TrainingFormValues } from '../types';

export const createTrainingCols = ({
  handleDelete,
  handleEdit,
}: {
  handleDelete: (id: number) => void;
  handleEdit: (id: number) => void;
}): ColDef[] => [
  createValidCol(),
  { colId: 'id', field: 'id', hide: true },
  {
    colId: 'facility',
    headerName: 'Facility',
    cellStyle: {
      display: 'flex',
      alignItems: 'flex-start',
      paddingTop: '8px',
      paddingBottom: '8px',
      lineHeight: '1.5',
    },
    valueGetter: ({ data }) => data?.facility?.label?.replace(/\s*\(.*\)$/, '') || '',
    comparator: (valueA: string, valueB: string) => valueA.localeCompare(valueB),
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      if (data) {
        const facilityName = data.facility?.label?.replace(/\s*\(.*\)$/, '') || '';
        const city = data.facility?.address?.city?.trim() || '';
        const state = data.facility?.address?.stateCode?.label?.trim() || '';
        const location = [city, state].filter(Boolean).join(', ');
        return (
          <div className="flex flex-col items-start text-left leading-tight">
            <span>{facilityName}</span>
            {location && <span className="text-grey-500 text-xs">{location}</span>}
          </div>
        );
      }
    },
  },
  { colId: 'trainingType', field: 'trainingType.label', headerName: 'Type' },
  {
    colId: 'specialtyBoardName',
    headerName: 'Training/Credentials',
    valueGetter: ({ data }) => data?.specialtyBoard?.label || '',
    comparator: (valueA: string, valueB: string) => valueA.localeCompare(valueB),
  },
  {
    colId: 'duration',
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      if (data) {
        const startDate = formatCredentialDate(data.startDate);
        const endDate = data.endDate ? formatCredentialDate(data.endDate) : 'Present';
        return `${startDate} – ${endDate}`;
      }
    },
    headerName: 'Duration',
    filter: 'agDateColumnFilter',
    valueGetter: ({ data }) => {
      if (data?.startDate) {
        return new Date(data.startDate);
      }
      return null;
    },
    comparator: dateComparator,
  },
  {
    colId: 'status',
    headerName: 'Status',
    valueGetter: ({ data }) => (data?.completedFlag ? 'Completed' : 'Incomplete'),
    comparator: (valueA: string, valueB: string) => valueA.localeCompare(valueB),
    cellRenderer: ({ data }: CustomCellRendererProps<TrainingFormValues>) => {
      if (data) {
        // MCV-1132: colored pills — green for Completed, amber for Incomplete —
        // so the status is scannable at a glance rather than two identical grey pills.
        return (
          <Pill
            label={data.completedFlag ? 'Completed' : 'Incomplete'}
            type={data.completedFlag ? 'success' : 'warning'}
          />
        );
      }
    },
  },
  {
    ...ACTIONS_COLUMN_BASE,
    width: 80,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        { type: 'edit', handler: handleEdit, cyTag: CT.privileges.table.deleteBtn },
        { type: 'delete', handler: handleDelete, cyTag: CT.privileges.table.deleteBtn },
      ] satisfies ActionDef[],
    },
  },
];
